/**
 * OnConflictCardEditable — the editContext-present render path of
 * `OnConflictCard` (§2.4 S6b). Structure-driven (extracted `OnConflictSpec`
 * with byte ranges), replacing the renderer-side string scan the no-context
 * card keeps:
 *   - accessory = shadcn Select action switch (DO NOTHING ↔ DO UPDATE,
 *     decision 9: switching TO do-update first collects a seed assignment
 *     through the standard `AssignmentAddPopover` form — `onSubmitAssignment`
 *     composes `setText` from its fields);
 *   - expanded body = conflict-target row (`ConflictTargetPicker`, editReady-
 *     gated) + `SetAssignmentsList` routed via `conflictTail="on-conflict"`.
 *
 * Extraction of `OnConflictCard` (file-size budget) — the dispatcher lives
 * there and mounts this only when editContext + a parsed onConflict exist.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import type { ParseOptions } from '../../../../sql-grammar/model';
import type {
  OnConflictAction,
  OnConflictSpec,
  QueryStructure,
} from '../../../../sql-resolver/types/queryStructure';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { modifyInsertConflictAction } from '../../edit/edits/insertConflictClause';
import { useVqeStore } from '../../store/vqeStore';
import { ClauseCard } from '../select/ClauseCard';
import { VqeTooltip } from '../common/VqeTooltip';
import { AssignmentAddPopover } from '../update/AssignmentAddPopover';
import { SetAssignmentsList } from '../update/SetAssignmentsList';
import { vqeCopy } from '../../ux/vqeCopy';
import { recordEditFailure, safeExtractStructure } from './insertColumnMapHelpers';
import { conflictAssignmentsSpan, conflictSummary } from './insertTailHelpers';
import { ConflictTargetPicker } from './ConflictTargetPicker';

export interface OnConflictCardEditableProps {
  readonly bufferText: string;
  readonly options: ParseOptions;
  /**
   * Commit-stable expand/collapse key seed (the workspace tab id) — see
   * `OnConflictCardProps.expansionScope`. A content-derived statement id here
   * would collapse the card after every commit made from its own body
   * (pill edit / add / remove), unmounting the very controls mid-flow.
   */
  readonly expansionScope: string;
  readonly editContext: VqeEditContext;
  readonly structure: QueryStructure;
  readonly onConflict: OnConflictSpec;
}

export function OnConflictCardEditable({
  bufferText,
  options,
  expansionScope,
  editContext,
  structure,
  onConflict,
}: OnConflictCardEditableProps) {
  const expansionKey = `${expansionScope}:on-conflict`;
  const expanded = useVqeStore((s) => s.expansion[expansionKey] ?? false);
  const setExpansion = useVqeStore((s) => s.setExpansion);
  // True while the DO NOTHING → DO UPDATE switch waits for its seed
  // assignment; the switch commits only when the seed form is submitted.
  const [seedingDoUpdate, setSeedingDoUpdate] = useState(false);

  const action = onConflict.action;
  const assignsSpan = conflictAssignmentsSpan(onConflict);
  const targetText = onConflict.targetRange
    ? bufferText.slice(onConflict.targetRange.startOffset, onConflict.targetRange.endOffset)
    : '(not set)';

  const onActionChange = (next: string): void => {
    if (next === action) return;
    if (next === 'do-nothing') {
      modifyInsertConflictAction('do-nothing', undefined, toCommitCtx(editContext), {
        structure: safeExtractStructure(editContext),
      }).catch(recordEditFailure);
      return;
    }
    // do-update requires a seed assignment (decision 9) — collect it first.
    setSeedingDoUpdate(true);
    setExpansion(expansionKey, true);
  };

  const submitSeed = async (target: string, valueText: string): Promise<void> => {
    await modifyInsertConflictAction(
      'do-update',
      `${target} = ${valueText}`,
      toCommitCtx(editContext),
      { structure: safeExtractStructure(editContext) },
    );
  };

  return (
    <ClauseCard
      title="On conflict"
      testId="vqe-insert-on-conflict"
      accessory={
        <Select value={action} onValueChange={onActionChange}>
          <SelectTrigger
            className="h-6 w-auto gap-1 border-amber-500/40 bg-amber-500/10 px-2 py-0 text-[10px] font-semibold uppercase tracking-wide text-amber-700 shadow-none dark:text-amber-400"
            data-testid="vqe-insert-on-conflict-action-select"
            aria-label="Conflict action"
          >
            <SelectValue>{actionLabel(action)}</SelectValue>
          </SelectTrigger>
          <SelectContent data-testid="vqe-insert-on-conflict-action-menu">
            <SelectItem value="do-nothing" data-testid="vqe-insert-on-conflict-action-option-do-nothing">
              {vqeCopy.picker.conflictActionDoNothing}
            </SelectItem>
            <SelectItem value="do-update" data-testid="vqe-insert-on-conflict-action-option-do-update">
              {vqeCopy.picker.conflictActionDoUpdate}
            </SelectItem>
          </SelectContent>
        </Select>
      }
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <VqeTooltip content={expanded ? 'Collapse' : 'Expand'}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => setExpansion(expansionKey, !expanded)}
              aria-expanded={expanded}
              data-testid="vqe-insert-on-conflict-toggle"
            >
              {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </Button>
          </VqeTooltip>
          <span className="flex-1 truncate font-mono text-xs" data-testid="vqe-insert-on-conflict-summary">
            {conflictSummary(bufferText, onConflict)}
          </span>
        </div>
        {seedingDoUpdate && action === 'do-nothing' ? (
          <div className="flex flex-col gap-1" data-testid="vqe-insert-on-conflict-seed">
            <p
              className="text-xs italic text-muted-foreground"
              data-testid="vqe-insert-on-conflict-seed-hint"
            >
              {vqeCopy.disabled.conflictDoUpdateNeedsAssignment}
            </p>
            <AssignmentAddPopover
              editContext={editContext}
              testIdPrefix="vqe-insert-on-conflict-seed"
              // Lists the INSERT target's columns + offers EXCLUDED.col; the
              // commit still goes through `onSubmitAssignment` (it wins).
              conflictTail="on-conflict"
              defaultOpen
              onOpenChange={(open) => {
                if (!open) setSeedingDoUpdate(false);
              }}
              onSubmitAssignment={submitSeed}
            />
          </div>
        ) : null}
        {expanded ? (
          <div className="rounded-md border border-border bg-background p-2" data-testid="vqe-insert-on-conflict-body">
            <div className="mb-2 flex items-center gap-2" data-testid="vqe-insert-on-conflict-target-row">
              <span className="text-xs text-muted-foreground">Target</span>
              <span className="font-mono text-xs" data-testid="vqe-insert-on-conflict-target-text">
                {targetText}
              </span>
              <ConflictTargetPicker
                editContext={editContext}
                structure={structure}
                onConflict={onConflict}
                testId="vqe-insert-on-conflict-target"
              />
            </div>
            {action === 'do-nothing' ? (
              <p className="text-xs italic text-muted-foreground" data-testid="vqe-insert-on-conflict-do-nothing">
                Conflict rows are ignored — no assignments.
              </p>
            ) : assignsSpan ? (
              <SetAssignmentsList
                bufferText={bufferText}
                start={assignsSpan.start}
                end={assignsSpan.end}
                options={options}
                testIdPrefix="vqe-insert-on-conflict"
                editContext={editContext}
                conflictTail="on-conflict"
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </ClauseCard>
  );
}

function actionLabel(action: OnConflictAction): string {
  return action === 'do-update'
    ? vqeCopy.picker.conflictActionDoUpdate
    : vqeCopy.picker.conflictActionDoNothing;
}
